import React, { useState } from 'react';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { ImSortAlphaAsc, ImSortAlphaDesc } from "react-icons/im";
import { MdDelete } from "react-icons/md";
import { Link } from 'react-router-dom';
import countries from 'i18n-iso-countries';
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));


const HealthCheckCard = ({ domain, pools, regions, refreshHealthCheck }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [sortOrder, setSortOrder] = useState('asc');
    const [showTooltip, setShowTooltip] = useState(false);

    const getCountryName = (isoCode) => {
        if (isoCode && countries.isValid(isoCode)) {
            return countries.getName(isoCode, 'en');
        }
        return 'Unknown';
    };

    const calculateSummary = () => {
        let totalOk = 0;
        let totalFail = 0;

        Object.entries(regions).forEach(([key, stats]) => {
            if (key !== 'pools') {
                totalOk += stats.ok;
                totalFail += stats.fail;
            }
        });

        return { totalOk, totalFail };
    };

    const { totalOk, totalFail } = calculateSummary();

    const toggleVisibility = () => {
        setIsVisible(!isVisible);
    };

    const toggleSortOrder = () => {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const sortedRegions = Object.entries(regions)
        .filter(([region]) => region !== 'pools')
        .sort(([regionA], [regionB]) => {
            const countryA = getCountryName(regionA);
            const countryB = getCountryName(regionB);
            if (sortOrder === 'asc') {
                return countryA.localeCompare(countryB);
            }
            return countryB.localeCompare(countryA);
        });

        return (
            <div className="min-w-full p-2">
                <div className="bg-white shadow-md rounded-lg border">
                    <div className="px-4 py-2 flex justify-between items-center gap-2">
                        <div className="p-1 flex flex-row gap-4 justify-between">
                            <h2 className="text-xl self-center font-semibold">{domain}</h2>
                            <div className="flex flex-wrap gap-2 items-center">
                                {pools.map((pool) => (
                                    <Link
                                        key={pool}
                                        to={`/pool/${pool}`}
                                        className="text-blue-600 hover:text-blue-800 font-medium mb-4 md:mb-0 transition-colors duration-200"
                                    >
                                        <h3 className="text-md text-blue-600 font-bold rounded-full bg-gray-100 px-2 hover:bg-gray-200">
                                            {pool}
                                        </h3>
                                    </Link>
                                ))}
                            </div>
                            <div className="flex gap-2 text-md items-center text-gray-500 min-w-fit">
                                <p className="font-bold text-lg">Total:</p>
                                <div className="flex items-center gap-1">
                                    Ok: {totalOk} <FaCheckCircle className="text-green-500" />
                                </div>
                                <div className="flex items-center gap-1">
                                    Fail: {totalFail} <FaTimesCircle className="text-red-500" />
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row gap-6 relative">
                            <button
                                className="aspect-w-1 aspect-h-1 text-blue-500 hover:text-blue-700 font-semibold relative"
                                onClick={() => refreshHealthCheck(domain)}
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                <MdDelete className="aspect-w-1 aspect-h-1 w-5 h-5" />
                                {showTooltip && (
                                    <span className="min-w-24 max-w-32 absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs px-2 py-1 rounded-md shadow-md">
                                        Drop health check info
                                    </span>
                                )}
                            </button>
                            <button
                                className="text-blue-500 hover:text-blue-700 min-w-14 font-semibold"
                                onClick={toggleVisibility}
                            >
                                {isVisible ? 'Hide' : 'Show'}
                            </button>
                        </div>
                    </div>
                    {isVisible && (
                        <div className="px-4 py-2">
                            <button
                                onClick={toggleSortOrder}
                                className="border flex flex-row px-3 py-2 rounded-lg gap-2 font-semibold text-gray-800 border-gray-300 hover:text-blue-500"
                            >
                                {sortOrder === 'asc' ? <ImSortAlphaAsc className="h-5 w-5" /> : <ImSortAlphaDesc className="h-5 w-5" />}
                            </button>
                            {sortedRegions.map(([region, stats]) => (
                                <div key={`${domain}-${region}`} className="border-b py-2">
                                    <div className="flex flex-row gap-12 items-center">
                                        <div className="font-medium min-w-80">
                                            <span>{getCountryName(region)} - {region}</span>
                                        </div>
                                        <div className="flex items-center min-w-24 gap-2">
                                            <FaCheckCircle className="text-green-500" />
                                            <span className="text-center">{stats.ok}</span>
                                        </div>
                                        <div className="flex items-center min-w-24 gap-2">
                                            <FaTimesCircle className="text-red-500" />
                                            <span className="text-center">{stats.fail}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
};

export default HealthCheckCard;
